import { Editor } from '@tiptap/react';
import { FloatingMenu } from '@tiptap/react/menus';
import { Heading1, Heading2, List, Quote, Minus } from 'lucide-react';

interface MarginMenuProps {
    editor: Editor | null;
}

export function MarginMenu({ editor }: MarginMenuProps) {
    if (!editor) return null;

    return (
        <FloatingMenu
            editor={editor}
            {...({ tippyOptions: { duration: 150, placement: 'left-start', offset: [0, 40] } } as any)}
            className="flex flex-col gap-1 p-1 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg shadow-xl overflow-hidden backdrop-blur-md bg-opacity-90 dark:bg-opacity-90 z-50"
            shouldShow={({ state, editor }: { state: any; editor: any }) => {
                // Must be focused, editable, and selection must be empty
                if (!editor.isFocused || !editor.isEditable) return false;

                const { selection } = state;
                const { $from } = selection;

                // Only show on empty paragraphs at the start of a block
                return $from.parent.content.size === 0 &&
                    $from.parent.type.name === 'paragraph' &&
                    $from.depth === 1; // Direct child of doc
            }}
        >
            <div className="px-2 py-1 text-[10px] font-bold text-zinc-400 uppercase tracking-widest border-b border-zinc-100 dark:border-zinc-800 mb-1">
                Insert
            </div>
            <button
                onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                className="flex items-center gap-2 p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded transition-colors text-zinc-600 dark:text-zinc-400"
            >
                <Heading1 size={14} />
                <span className="text-xs">Large Heading</span>
            </button>
            <button
                onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                className="flex items-center gap-2 p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded transition-colors text-zinc-600 dark:text-zinc-400"
            >
                <Heading2 size={14} />
                <span className="text-xs">Subheading</span>
            </button>
            <button
                onClick={() => editor.chain().focus().toggleBulletList().run()}
                className="flex items-center gap-2 p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded transition-colors text-zinc-600 dark:text-zinc-400"
            >
                <List size={14} />
                <span className="text-xs">Bullet List</span>
            </button>
            <button
                onClick={() => editor.chain().focus().toggleBlockquote().run()}
                className="flex items-center gap-2 p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded transition-colors text-zinc-600 dark:text-zinc-400"
            >
                <Quote size={14} />
                <span className="text-xs">Quote Block</span>
            </button>
            <button
                onClick={() => editor.chain().focus().setHorizontalRule().run()}
                className="flex items-center gap-2 p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded transition-colors text-zinc-600 dark:text-zinc-400"
            >
                <Minus size={14} />
                <span className="text-xs">Divider</span>
            </button>
        </FloatingMenu>
    );
}
